'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:tillamook-com":true});}catch(e){}}();

import React from 'react';

import cx from 'classnames';
import NextImage from 'next/image';
import PropTypes from 'prop-types';

import Lockup from 'components/blocks/shared/Lockup';
import Parallax from 'components/ui/Parallax';
import Video from 'components/ui/Video';
import VideoPlayerYT from 'components/ui/VideoPlayerYT';

import { useTheme } from 'hooks/useTheme';
import { blockRootProps, getColor } from 'utils';
import { addProtocol } from 'utils/url';

import styles from './BlockSmallMediaWithText.module.scss';

const themeMap = {
    'Landscape Image': 'imageLandscape',
    'Portrait Image': 'imagePortrait',
};

const headlineOverrides = {
    'makers-reserve': {
        baseTheme: 'makers36',
        themes: { large: 'makers60' },
    },
};

const BlockSmallMediaWithText = ({
    __typename,
    anchorId,
    icon,
    eyebrow,
    headline,
    body,
    cta,
    image,
    mediaPosition,
    theme,
    videoTextureMp4,
    videoTextureWebm,
    videoPoster,
    youtubeVideoId,
    textAlignment,
    flushTop,
    flushBottom,
    backgroundColor,
    lockupTheme,
}) => {
    mediaPosition ??= 'Left';
    textAlignment ??= 'Left';
    theme ??= 'Landscape Image';
    lockupTheme ??= 'Medium';

    const hasImage = image?.url;
    const hasVideo = youtubeVideoId;
    const hasTexturalVideo =
        videoTextureMp4?.url && videoTextureWebm?.url && !hasVideo;

    const [globalTheme] = useTheme();

    return (
        <section
            style={
                backgroundColor
                    ? {
                          '--background-color': getColor(backgroundColor),
                      }
                    : undefined
            }
            {...blockRootProps(anchorId, __typename)}
            className={cx(styles.root, styles[themeMap[theme]], {
                [styles.imageRight]: mediaPosition.toLowerCase() === 'right',
                [styles.textAlignLeft]: textAlignment?.toLowerCase() === 'left',
                [styles.flushTop]: flushTop,
                [styles.flushBottom]: flushBottom,
                [styles.makersReserve]: globalTheme === 'makers-reserve',
            })}
        >
            <div className={styles.inner}>
                <div className={styles.media}>
                    {hasImage && (
                        <Parallax className={styles.parallaxImage}>
                            <NextImage
                                className={styles.image}
                                src={addProtocol(image?.url)}
                                sizes="(max-width: 768px) 100vw, (max-width: 1440px) 50vw, 33vw"
                                width={image?.width}
                                height={image?.height}
                                alt={image?.description || image?.title || ''}
                            />
                        </Parallax>
                    )}
                    {hasVideo && (
                        <div className={styles.videoPlayerAspect}>
                            <div className={styles.videoPlayer}>
                                <VideoPlayerYT
                                    centerPlayButton
                                    heroVideo={true}
                                    youtubeVideoId={youtubeVideoId}
                                    videoTexturePoster={videoPoster}
                                    videoTextureMp4={videoTextureMp4}
                                    videoTextureWebm={videoTextureWebm}
                                    threshold={0.25}
                                />
                            </div>
                        </div>
                    )}
                    {hasTexturalVideo && (
                        <Video
                            poster={videoPoster?.url}
                            sources={[
                                videoTextureWebm.url,
                                videoTextureMp4.url,
                            ]}
                        />
                    )}
                </div>
                <div className={styles.card}>
                    <div className={styles.cardInner}>
                        <Lockup
                            icon={icon}
                            eyebrow={{ text: eyebrow }}
                            headline={{
                                text: headline,
                                themeOverrides: headlineOverrides[globalTheme],
                            }}
                            copy={{ text: body }}
                            ctas={cta}
                            theme={lockupTheme}
                        />
                    </div>
                </div>
            </div>
        </section>
    );
};

BlockSmallMediaWithText.propTypes = {
    __typename: PropTypes.string,
    anchorId: PropTypes.string,
    backgroundColor: PropTypes.string,
    body: PropTypes.object,
    cta: PropTypes.object,
    displayArrowIcon: PropTypes.bool,
    eyebrow: PropTypes.string,
    flushBottom: PropTypes.bool,
    flushTop: PropTypes.bool,
    headline: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    icon: PropTypes.object,
    image: PropTypes.shape({
        height: PropTypes.number,
        url: PropTypes.string,
        width: PropTypes.number,
    }),
    mediaPosition: PropTypes.oneOf(['Left', 'Right']),
    theme: PropTypes.oneOf(['Landscape Image', 'Portrait Image']),
    textAlignment: PropTypes.oneOf(['Left', 'Center']),
    videoPoster: PropTypes.shape({
        height: PropTypes.number,
        url: PropTypes.string,
        width: PropTypes.number,
    }),
    videoTextureMp4: PropTypes.shape({
        url: PropTypes.string,
    }),
    videoTextureWebm: PropTypes.shape({
        url: PropTypes.string,
    }),
    youtubeVideoId: PropTypes.string,
};

export default BlockSmallMediaWithText;
